import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type FilterType = 'TODOS' | 'MOTORISTAS' | 'PASSAGEIROS';

interface Tutorial {
  id: number;
  title: string;
  duration: string;
  segment: FilterType | 'TODOS';
  icon: string;
  color: string;
  link: string;
}

interface TutorialFilter {
  label: string;
  value: FilterType;
  icon: string;
}

const CARD_W = 280;
const SIDE_OFF = 308;
const AUTO_DELAY = 2800;

const segmentColor: Record<FilterType, string> = {
  TODOS: '#00C4F0',
  MOTORISTAS: '#00C4F0',
  PASSAGEIROS: '#4FC3F7',
};

const defaultSegmentLabels: Record<FilterType, string> = {
  TODOS: 'All',
  MOTORISTAS: 'Drivers',
  PASSAGEIROS: 'Riders',
};

const TutorialsSection = () => {
  const { t } = useTranslation();
  const filtersRaw = t('home.tutorials.filters', { returnObjects: true });
  const tutorialsRaw = t('home.tutorials.cards', { returnObjects: true });
  const segmentLabelsRaw = t('home.tutorials.segmentLabels', { returnObjects: true });

  const filters = Array.isArray(filtersRaw)
    ? (filtersRaw as TutorialFilter[])
    : [];
  const tutorials = Array.isArray(tutorialsRaw)
    ? (tutorialsRaw as Tutorial[])
    : [];
  const segmentLabels =
    typeof segmentLabelsRaw === 'object' && segmentLabelsRaw !== null
      ? (segmentLabelsRaw as Record<string, string>)
      : {};

  const [activeFilter, setActiveFilter] = useState<FilterType>(
    filters.some((filter) => filter.value === 'TODOS')
      ? 'TODOS'
      : 'MOTORISTAS',
  );
  const [activeIdx, setActiveIdx] = useState(0);
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const filtered = activeFilter === 'TODOS'
    ? tutorials
    : tutorials.filter(
        (tutorial) =>
          tutorial.segment === activeFilter || tutorial.segment === 'TODOS',
      );
  const total = filtered.length;

  const stopAuto = () => {
    if (autoRef.current) clearInterval(autoRef.current);
  };

  const startAuto = useCallback(() => {
    stopAuto();
    if (total === 0) {
      return;
    }
    autoRef.current = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % total);
    }, AUTO_DELAY);
  }, [total]);

  useEffect(() => {
    setActiveIdx(0);
    startAuto();
    return stopAuto;
  }, [activeFilter, startAuto]);

  const goNext = () => {
    if (total === 0) return;
    stopAuto();
    setActiveIdx((p) => (p + 1) % total);
    startAuto();
  };

  const goPrev = () => {
    if (total === 0) return;
    stopAuto();
    setActiveIdx((p) => (p - 1 + total) % total);
    startAuto();
  };

  const goTo = (i: number) => {
    if (total === 0) return;
    stopAuto();
    setActiveIdx(i);
    startAuto();
  };

  const getOffset = (i: number) => {
    if (total === 0) return 0;
    let diff = (i - activeIdx + total) % total;
    if (diff > total / 2) diff -= total;
    return diff;
  };

  const getSegmentLabel = (segment: string) =>
    segmentLabels[segment] ?? defaultSegmentLabels[segment as FilterType] ?? segment;

  return (
    <section id="tutoriais" className="w-full bg-[#F8FAFF] py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 bg-white text-[#091D42] text-xs font-semibold px-3 py-1.5 rounded-full mb-5 tracking-widest uppercase border border-[#E0EAF4]">
            <i className="ri-play-circle-line text-[#E8365D]" />
            {t('home.tutorials.badge')}
          </div>
          <h2
            className="font-black text-[#091D42] leading-tight mb-3"
            style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(2.4rem, 5vw, 3.8rem)' }}
          >
            {t('home.tutorials.title')}
          </h2>
          <p className="font-bold text-[#00C4F0] mb-2" style={{ fontSize: 'clamp(1.35rem, 3vw, 2rem)' }}>
            {t('home.tutorials.subtitle')}
          </p>
          <p className="text-[#091D42]/50 text-base sm:text-lg max-w-lg mx-auto">
            {t('home.tutorials.description')}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-row items-center justify-center gap-3 mb-14">
          {filters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setActiveFilter(filter.value)}
              className={`flex items-center gap-1.5 sm:gap-3 px-3 sm:px-7 py-2 sm:py-3 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeFilter === filter.value
                  ? 'bg-[#091D42] text-white'
                  : 'bg-white text-[#091D42]/60 hover:bg-[#E8F4FF] border border-[#E0EAF4]'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${filter.icon} text-base`} />
              </div>
              {filter.label}
              {activeFilter === filter.value && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#E8365D] ml-1" />
              )}
            </button>
          ))}
        </div>

        {/* 3-card carousel */}
        <div className="relative flex items-center justify-center">
          {/* Prev */}
          <button
            onClick={goPrev}
            className="absolute left-0 sm:left-4 z-20 w-10 h-10 rounded-full flex items-center justify-center cursor-pointer whitespace-nowrap transition-all duration-200"
            style={{ background: 'rgba(9,29,66,0.07)', border: '1px solid rgba(9,29,66,0.12)' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = '#091D42';
              (e.currentTarget as HTMLButtonElement).style.color = 'white';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(9,29,66,0.07)';
              (e.currentTarget as HTMLButtonElement).style.color = '';
            }}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-s-line text-xl" />
            </div>
          </button>

          {/* Stage */}
          <div
            className="relative select-none"
            style={{ height: '360px', width: '100%', maxWidth: '960px', overflow: 'hidden' }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              {filtered.map((tutorial, i) => {
                const offset = getOffset(i);
                const isActive = offset === 0;
                const visible = Math.abs(offset) <= 1;
                return (
                  <div
                    key={tutorial.id}
                    onClick={() => !isActive && goTo(i)}
                    style={{
                      position: 'absolute',
                      width: `${CARD_W}px`,
                      height: '300px',
                      transform: `translateX(${offset * SIDE_OFF}px) scale(${isActive ? 1 : 0.84})`,
                      opacity: visible ? (isActive ? 1 : 0.58) : 0,
                      transition: 'transform 0.55s cubic-bezier(0.4,0,0.2,1), opacity 0.55s ease',
                      zIndex: isActive ? 10 : visible ? 5 : 0,
                      pointerEvents: visible ? 'auto' : 'none',
                      cursor: isActive ? 'default' : 'pointer',
                    }}
                  >
                    <div
                      className="w-full h-full rounded-2xl p-6 flex flex-col"
                      style={{
                        background: isActive ? '#091D42' : 'white',
                        border: isActive ? 'none' : '1px solid #E8F0F8',
                        boxShadow: isActive ? '0 24px 64px rgba(0,196,240,0.18)' : '0 4px 24px rgba(9,29,66,0.07)',
                      }}
                    >
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 flex-shrink-0"
                        style={{ background: isActive ? 'rgba(0,212,255,0.15)' : '#EFF8FF' }}
                      >
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className={`${tutorial.icon} text-xl`} style={{ color: tutorial.color }} />
                        </div>
                      </div>

                      <h3
                        className={`font-bold text-base leading-snug mb-3 ${isActive ? 'text-white' : 'text-[#091D42]'}`}
                      >
                        {tutorial.title}
                      </h3>

                      <div className="flex-1">
                        <span
                          className="inline-block text-xs font-bold px-3 py-1 rounded-full"
                          style={{
                            background: isActive ? 'rgba(0,196,240,0.18)' : `${segmentColor[tutorial.segment as FilterType]}22`,
                            color: segmentColor[tutorial.segment as FilterType],
                          }}
                        >
                          {getSegmentLabel(tutorial.segment)}
                        </span>
                      </div>

                      <a
                        href={tutorial.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`mt-4 flex items-center gap-2 text-sm font-semibold cursor-pointer whitespace-nowrap ${isActive ? 'text-[#00C4F0]' : 'text-[#091D42]/50'}`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-play-fill" />
                        </div>
                        {t('home.tutorials.watchLabel')}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Next */}
          <button
            onClick={goNext}
            className="absolute right-0 sm:right-4 z-20 w-10 h-10 rounded-full flex items-center justify-center cursor-pointer whitespace-nowrap transition-all duration-200"
            style={{ background: 'rgba(9,29,66,0.07)', border: '1px solid rgba(9,29,66,0.12)' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = '#091D42';
              (e.currentTarget as HTMLButtonElement).style.color = 'white';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(9,29,66,0.07)';
              (e.currentTarget as HTMLButtonElement).style.color = '';
            }}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xl" />
            </div>
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {filtered.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="cursor-pointer transition-all duration-300 rounded-full"
              style={{
                width: i === activeIdx ? '24px' : '8px',
                height: '8px',
                background: i === activeIdx ? '#E8365D' : 'rgba(9,29,66,0.2)',
              }}
            />
          ))}
        </div>

      </div>
    </section>
  );
};

export default TutorialsSection;
